'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    CONDITION_STATUS_LABELS,
    ConditionStatus,
    CreateFamilyHistoryData,
    CreatePersonalHistoryData,
    FAMILY_RELATIONSHIP_LABELS,
    FamilyHistory,
    FamilyRelationship,
    IcdCode,
    PERSONAL_HISTORY_CATEGORY_LABELS,
    PersonalHistory,
    PersonalHistoryCategory,
    UpdateFamilyHistoryData,
    UpdatePersonalHistoryData,
    createFamilyHistory,
    createPersonalHistory,
    deleteFamilyHistory,
    deletePersonalHistory,
    getFamilyHistoryByClient,
    getPersonalHistoryByClient,
    searchIcdCodes,
    updateFamilyHistory,
    updatePersonalHistory,
} from '@/lib/api/prontuario/prontuario-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdFamilyRestroom,
    MdHistory,
    MdPerson,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

type TabType = 'personal' | 'family'

interface PersonalHistoryFormData {
    category: PersonalHistoryCategory
    condition: string
    icdCodeId: number | null
    diagnosisDate: string
    resolutionDate: string
    status: ConditionStatus
    severity: string
    treatment: string
    currentMedications: string
    notes: string
    professionalId: string
}

interface FamilyHistoryFormData {
    relationship: FamilyRelationship
    condition: string
    icdCodeId: number | null
    ageAtOnset: string
    deceased: boolean
    deathAge: string
    deathCause: string
    notes: string
    professionalId: string
}

const defaultPersonalFormData: PersonalHistoryFormData = {
    category: PersonalHistoryCategory.OTHER,
    condition: '',
    icdCodeId: null,
    diagnosisDate: '',
    resolutionDate: '',
    status: ConditionStatus.ACTIVE,
    severity: '',
    treatment: '',
    currentMedications: '',
    notes: '',
    professionalId: '',
}

const defaultFamilyFormData: FamilyHistoryFormData = {
    relationship: FamilyRelationship.OTHER,
    condition: '',
    icdCodeId: null,
    ageAtOnset: '',
    deceased: false,
    deathAge: '',
    deathCause: '',
    notes: '',
    professionalId: '',
}

export default function AntecedentesPage() {
    const { client, clientId } = useProntuarioData()
    const { user, isProfessional } = useAuth()

    const [activeTab, setActiveTab] = useState<TabType>('personal')
    const [personalHistory, setPersonalHistory] = useState<PersonalHistory[]>(
        []
    )
    const [familyHistory, setFamilyHistory] = useState<FamilyHistory[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    // Personal History Modal
    const [personalModalOpen, setPersonalModalOpen] = useState(false)
    const [editingPersonalId, setEditingPersonalId] = useState<number | null>(
        null
    )
    const [personalFormData, setPersonalFormData] =
        useState<PersonalHistoryFormData>(defaultPersonalFormData)

    // Family History Modal
    const [familyModalOpen, setFamilyModalOpen] = useState(false)
    const [editingFamilyId, setEditingFamilyId] = useState<number | null>(null)
    const [familyFormData, setFamilyFormData] = useState<FamilyHistoryFormData>(
        defaultFamilyFormData
    )

    // ICD Search
    const [icdSearchQuery, setIcdSearchQuery] = useState('')
    const [icdSearchResults, setIcdSearchResults] = useState<IcdCode[]>([])
    const [selectedIcd, setSelectedIcd] = useState<IcdCode | null>(null)
    const [searchingIcd, setSearchingIcd] = useState(false)
    const [showDeletePersonalModal, setShowDeletePersonalModal] =
        useState(false)
    const [personalHistoryToDelete, setPersonalHistoryToDelete] = useState<
        number | null
    >(null)
    const [showDeleteFamilyModal, setShowDeleteFamilyModal] = useState(false)
    const [familyHistoryToDelete, setFamilyHistoryToDelete] = useState<
        number | null
    >(null)

    useEffect(() => {
        if (clientId) {
            loadData()
        }
    }, [clientId])

    const loadData = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const [personalResponse, familyResponse] = await Promise.all([
                getPersonalHistoryByClient(parseInt(clientId, 10)),
                getFamilyHistoryByClient(parseInt(clientId, 10)),
            ])

            if (personalResponse.success) {
                setPersonalHistory(personalResponse.result || [])
            }

            if (familyResponse.success) {
                setFamilyHistory(familyResponse.result || [])
            }
        } catch (error) {
            toast.error('Erro ao carregar antecedentes')
        } finally {
            setLoading(false)
        }
    }

    const handleIcdSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setIcdSearchResults([])
            return
        }

        setSearchingIcd(true)
        try {
            const response = await searchIcdCodes(query)
            if (response.success) {
                setIcdSearchResults(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao buscar CID:', error)
        } finally {
            setSearchingIcd(false)
        }
    }, [])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleIcdSearch(icdSearchQuery)
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [icdSearchQuery, handleIcdSearch])

    const selectIcd = (icd: IcdCode) => {
        setSelectedIcd(icd)
        setIcdSearchQuery('')
        setIcdSearchResults([])
        if (personalModalOpen) {
            setPersonalFormData(prev => ({ ...prev, icdCodeId: icd.id }))
        } else if (familyModalOpen) {
            setFamilyFormData(prev => ({ ...prev, icdCodeId: icd.id }))
        }
    }

    const clearSelectedIcd = () => {
        setSelectedIcd(null)
        if (personalModalOpen) {
            setPersonalFormData(prev => ({ ...prev, icdCodeId: null }))
        } else if (familyModalOpen) {
            setFamilyFormData(prev => ({ ...prev, icdCodeId: null }))
        }
    }

    // Personal History Functions
    const openPersonalModal = (item?: PersonalHistory) => {
        if (item) {
            setEditingPersonalId(item.id)
            setPersonalFormData({
                category: item.category,
                condition: item.condition,
                icdCodeId: item.icdCodeId || null,
                diagnosisDate: item.diagnosisDate
                    ? item.diagnosisDate.split('T')[0]
                    : '',
                resolutionDate: item.resolutionDate
                    ? item.resolutionDate.split('T')[0]
                    : '',
                status: item.status,
                severity: item.severity || '',
                treatment: item.treatment || '',
                currentMedications: item.currentMedications || '',
                notes: item.notes || '',
                professionalId: item.registeredById
                    ? String(item.registeredById)
                    : '',
            })
            if (item.icdCode) {
                setSelectedIcd(item.icdCode)
            }
        } else {
            setEditingPersonalId(null)
            setPersonalFormData(defaultPersonalFormData)
            setSelectedIcd(null)
        }
        setPersonalModalOpen(true)
    }

    const closePersonalModal = () => {
        setPersonalModalOpen(false)
        setEditingPersonalId(null)
        setPersonalFormData(defaultPersonalFormData)
        setSelectedIcd(null)
        setIcdSearchQuery('')
        setIcdSearchResults([])
    }

    const handlePersonalSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isProfessional && !personalFormData.professionalId) {
            toast.error('Selecione um profissional responsável')
            return
        }

        if (!personalFormData.condition.trim()) {
            toast.error('Informe a condição')
            return
        }

        if (!clientId) return

        setSaving(true)
        try {
            if (editingPersonalId) {
                const updateData: UpdatePersonalHistoryData = {
                    category: personalFormData.category,
                    condition: personalFormData.condition.trim(),
                    icdCodeId: personalFormData.icdCodeId || undefined,
                    diagnosisDate: personalFormData.diagnosisDate || undefined,
                    resolutionDate:
                        personalFormData.resolutionDate || undefined,
                    status: personalFormData.status,
                    severity: personalFormData.severity.trim() || undefined,
                    treatment: personalFormData.treatment.trim() || undefined,
                    currentMedications:
                        personalFormData.currentMedications.trim() || undefined,
                    notes: personalFormData.notes.trim() || undefined,
                }

                const response = await updatePersonalHistory(
                    editingPersonalId,
                    updateData
                )
                if (response.success) {
                    toast.success('Antecedente atualizado com sucesso!')
                    await loadData()
                    closePersonalModal()
                } else {
                    toast.error(
                        response.error || 'Erro ao atualizar antecedente'
                    )
                }
            } else {
                const createData: CreatePersonalHistoryData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    registeredById: isProfessional
                        ? user!.professionalId!
                        : parseInt(personalFormData.professionalId, 10),
                    category: personalFormData.category,
                    condition: personalFormData.condition.trim(),
                    icdCodeId: personalFormData.icdCodeId || undefined,
                    diagnosisDate: personalFormData.diagnosisDate || undefined,
                    resolutionDate:
                        personalFormData.resolutionDate || undefined,
                    status: personalFormData.status,
                    severity: personalFormData.severity.trim() || undefined,
                    treatment: personalFormData.treatment.trim() || undefined,
                    currentMedications:
                        personalFormData.currentMedications.trim() || undefined,
                    notes: personalFormData.notes.trim() || undefined,
                }

                const response = await createPersonalHistory(createData)
                if (response.success) {
                    toast.success('Antecedente registrado com sucesso!')
                    await loadData()
                    closePersonalModal()
                } else {
                    toast.error(
                        response.error || 'Erro ao registrar antecedente'
                    )
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar antecedente')
        } finally {
            setSaving(false)
        }
    }

    const handleDeletePersonal = (id: number) => {
        setPersonalHistoryToDelete(id)
        setShowDeletePersonalModal(true)
    }

    const confirmDeletePersonal = async () => {
        if (!personalHistoryToDelete) return

        try {
            const response = await deletePersonalHistory(
                personalHistoryToDelete
            )
            if (response.success) {
                toast.success('Antecedente excluído com sucesso!')
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao excluir antecedente')
            }
        } catch (error) {
            toast.error('Erro ao excluir antecedente')
        } finally {
            setShowDeletePersonalModal(false)
            setPersonalHistoryToDelete(null)
        }
    }

    // Family History Functions
    const openFamilyModal = (item?: FamilyHistory) => {
        if (item) {
            setEditingFamilyId(item.id)
            setFamilyFormData({
                relationship: item.relationship,
                condition: item.condition,
                icdCodeId: item.icdCodeId || null,
                ageAtOnset: item.ageAtOnset?.toString() || '',
                deceased: item.deceased,
                deathAge: item.deathAge?.toString() || '',
                deathCause: item.deathCause || '',
                notes: item.notes || '',
                professionalId: item.registeredById
                    ? String(item.registeredById)
                    : '',
            })
            if (item.icdCode) {
                setSelectedIcd(item.icdCode)
            }
        } else {
            setEditingFamilyId(null)
            setFamilyFormData(defaultFamilyFormData)
            setSelectedIcd(null)
        }
        setFamilyModalOpen(true)
    }

    const closeFamilyModal = () => {
        setFamilyModalOpen(false)
        setEditingFamilyId(null)
        setFamilyFormData(defaultFamilyFormData)
        setSelectedIcd(null)
        setIcdSearchQuery('')
        setIcdSearchResults([])
    }

    const handleFamilySubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isProfessional && !familyFormData.professionalId) {
            toast.error('Selecione um profissional responsável')
            return
        }

        if (!familyFormData.condition.trim()) {
            toast.error('Informe a condição')
            return
        }

        if (!clientId) return

        setSaving(true)
        try {
            if (editingFamilyId) {
                const updateData: UpdateFamilyHistoryData = {
                    relationship: familyFormData.relationship,
                    condition: familyFormData.condition.trim(),
                    icdCodeId: familyFormData.icdCodeId || undefined,
                    ageAtOnset: familyFormData.ageAtOnset
                        ? parseInt(familyFormData.ageAtOnset, 10)
                        : undefined,
                    deceased: familyFormData.deceased,
                    deathAge: familyFormData.deathAge
                        ? parseInt(familyFormData.deathAge, 10)
                        : undefined,
                    deathCause: familyFormData.deathCause.trim() || undefined,
                    notes: familyFormData.notes.trim() || undefined,
                }

                const response = await updateFamilyHistory(
                    editingFamilyId,
                    updateData
                )
                if (response.success) {
                    toast.success(
                        'Antecedente familiar atualizado com sucesso!'
                    )
                    await loadData()
                    closeFamilyModal()
                } else {
                    toast.error(
                        response.error ||
                            'Erro ao atualizar antecedente familiar'
                    )
                }
            } else {
                const createData: CreateFamilyHistoryData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    registeredById: isProfessional
                        ? user!.professionalId!
                        : parseInt(familyFormData.professionalId, 10),
                    relationship: familyFormData.relationship,
                    condition: familyFormData.condition.trim(),
                    icdCodeId: familyFormData.icdCodeId || undefined,
                    ageAtOnset: familyFormData.ageAtOnset
                        ? parseInt(familyFormData.ageAtOnset, 10)
                        : undefined,
                    deceased: familyFormData.deceased,
                    deathAge: familyFormData.deathAge
                        ? parseInt(familyFormData.deathAge, 10)
                        : undefined,
                    deathCause: familyFormData.deathCause.trim() || undefined,
                    notes: familyFormData.notes.trim() || undefined,
                }

                const response = await createFamilyHistory(createData)
                if (response.success) {
                    toast.success(
                        'Antecedente familiar registrado com sucesso!'
                    )
                    await loadData()
                    closeFamilyModal()
                } else {
                    toast.error(
                        response.error ||
                            'Erro ao registrar antecedente familiar'
                    )
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar antecedente familiar')
        } finally {
            setSaving(false)
        }
    }

    const handleDeleteFamily = (id: number) => {
        setFamilyHistoryToDelete(id)
        setShowDeleteFamilyModal(true)
    }

    const confirmDeleteFamily = async () => {
        if (!familyHistoryToDelete) return

        try {
            const response = await deleteFamilyHistory(familyHistoryToDelete)
            if (response.success) {
                toast.success('Antecedente familiar excluído com sucesso!')
                await loadData()
            } else {
                toast.error(
                    response.error || 'Erro ao excluir antecedente familiar'
                )
            }
        } catch (error) {
            toast.error('Erro ao excluir antecedente familiar')
        } finally {
            setShowDeleteFamilyModal(false)
            setFamilyHistoryToDelete(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando antecedentes...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header with Tabs */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                <div className="tabs tabs-bordered px-4">
                    <button
                        onClick={() => setActiveTab('personal')}
                        className={`tab gap-2 ${activeTab === 'personal' ? 'tab-active' : ''}`}
                    >
                        <MdPerson className="text-lg" />
                        Antecedentes Pessoais
                        <span className="badge badge-sm">
                            {personalHistory.length}
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('family')}
                        className={`tab gap-2 ${activeTab === 'family' ? 'tab-active' : ''}`}
                    >
                        <MdFamilyRestroom className="text-lg" />
                        Antecedentes Familiares
                        <span className="badge badge-sm">
                            {familyHistory.length}
                        </span>
                    </button>
                </div>
            </div>

            {/* Personal History Tab */}
            {activeTab === 'personal' && (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-base-content">
                                Antecedentes Pessoais
                            </h2>
                            <p className="text-sm text-base-content/60">
                                Histórico de condições médicas do paciente
                            </p>
                        </div>
                        <button
                            onClick={() => openPersonalModal()}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Novo Antecedente
                        </button>
                    </div>

                    {personalHistory.length === 0 ? (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                            <MdHistory className="text-4xl mx-auto mb-3 text-base-content/20" />
                            <p className="text-base-content/60">
                                Nenhum antecedente pessoal registrado
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {personalHistory.map(item => (
                                <div
                                    key={item.id}
                                    className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm p-5"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-bold text-base-content">
                                                    {item.condition}
                                                </h3>
                                                <span className="badge badge-sm badge-ghost">
                                                    {
                                                        PERSONAL_HISTORY_CATEGORY_LABELS[
                                                            item.category
                                                        ]
                                                    }
                                                </span>
                                                <span
                                                    className={`badge badge-sm ${
                                                        item.status ===
                                                        ConditionStatus.ACTIVE
                                                            ? 'badge-error'
                                                            : item.status ===
                                                                ConditionStatus.RESOLVED
                                                              ? 'badge-success'
                                                              : 'badge-warning'
                                                    }`}
                                                >
                                                    {
                                                        CONDITION_STATUS_LABELS[
                                                            item.status
                                                        ]
                                                    }
                                                </span>
                                            </div>
                                            {item.icdCode && (
                                                <p className="text-sm text-base-content/60 mb-2">
                                                    CID: {item.icdCode.code} -{' '}
                                                    {item.icdCode.description}
                                                </p>
                                            )}
                                            {item.treatment && (
                                                <p className="text-sm text-base-content/70 mb-1">
                                                    <strong>Tratamento:</strong>{' '}
                                                    {item.treatment}
                                                </p>
                                            )}
                                            {item.currentMedications && (
                                                <p className="text-sm text-base-content/70 mb-1">
                                                    <strong>
                                                        Medicamentos:
                                                    </strong>{' '}
                                                    {item.currentMedications}
                                                </p>
                                            )}
                                            {item.notes && (
                                                <p className="text-sm text-base-content/60 line-clamp-2">
                                                    {item.notes}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-4 mt-3 text-xs text-base-content/50">
                                                {item.diagnosisDate && (
                                                    <span>
                                                        Diagnóstico:{' '}
                                                        {format(
                                                            new Date(
                                                                item.diagnosisDate
                                                            ),
                                                            'dd/MM/yyyy',
                                                            { locale: ptBR }
                                                        )}
                                                    </span>
                                                )}
                                                <span>
                                                    Registrado:{' '}
                                                    {format(
                                                        new Date(
                                                            item.createdAt
                                                        ),
                                                        'dd/MM/yyyy',
                                                        { locale: ptBR }
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() =>
                                                    openPersonalModal(item)
                                                }
                                                className="btn btn-ghost btn-sm btn-square"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDeletePersonal(
                                                        item.id
                                                    )
                                                }
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Family History Tab */}
            {activeTab === 'family' && (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-base-content">
                                Antecedentes Familiares
                            </h2>
                            <p className="text-sm text-base-content/60">
                                Histórico de condições médicas na família
                            </p>
                        </div>
                        <button
                            onClick={() => openFamilyModal()}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Novo Antecedente
                        </button>
                    </div>

                    {familyHistory.length === 0 ? (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                            <MdFamilyRestroom className="text-4xl mx-auto mb-3 text-base-content/20" />
                            <p className="text-base-content/60">
                                Nenhum antecedente familiar registrado
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {familyHistory.map(item => (
                                <div
                                    key={item.id}
                                    className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm p-5"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-bold text-base-content">
                                                    {item.condition}
                                                </h3>
                                                <span className="badge badge-sm badge-info">
                                                    {
                                                        FAMILY_RELATIONSHIP_LABELS[
                                                            item.relationship
                                                        ]
                                                    }
                                                </span>
                                                {item.deceased && (
                                                    <span className="badge badge-sm badge-ghost">
                                                        Falecido
                                                    </span>
                                                )}
                                            </div>
                                            {item.icdCode && (
                                                <p className="text-sm text-base-content/60 mb-2">
                                                    CID: {item.icdCode.code} -{' '}
                                                    {item.icdCode.description}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap gap-4 text-sm text-base-content/70">
                                                {item.ageAtOnset && (
                                                    <span>
                                                        Idade de início:{' '}
                                                        {item.ageAtOnset} anos
                                                    </span>
                                                )}
                                                {item.deceased &&
                                                    item.deathAge && (
                                                        <span>
                                                            Idade ao falecer:{' '}
                                                            {item.deathAge} anos
                                                        </span>
                                                    )}
                                                {item.deceased &&
                                                    item.deathCause && (
                                                        <span>
                                                            Causa:{' '}
                                                            {item.deathCause}
                                                        </span>
                                                    )}
                                            </div>
                                            {item.notes && (
                                                <p className="text-sm text-base-content/60 line-clamp-2 mt-2">
                                                    {item.notes}
                                                </p>
                                            )}
                                            <div className="text-xs text-base-content/50 mt-3">
                                                Registrado:{' '}
                                                {format(
                                                    new Date(item.createdAt),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() =>
                                                    openFamilyModal(item)
                                                }
                                                className="btn btn-ghost btn-sm btn-square"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDeleteFamily(item.id)
                                                }
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Personal History Modal */}
            {personalModalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingPersonalId
                                    ? 'Editar Antecedente Pessoal'
                                    : 'Novo Antecedente Pessoal'}
                            </h3>
                            <button
                                onClick={closePersonalModal}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form
                            onSubmit={handlePersonalSubmit}
                            className="space-y-4"
                        >
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Profissional Responsável{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={
                                            personalFormData.professionalId
                                                ? Number(
                                                      personalFormData.professionalId
                                                  )
                                                : null
                                        }
                                        onChange={id =>
                                            setPersonalFormData(prev => ({
                                                ...prev,
                                                professionalId: id
                                                    ? String(id)
                                                    : '',
                                            }))
                                        }
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={!!editingPersonalId}
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Categoria{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={personalFormData.category}
                                        onChange={e =>
                                            setPersonalFormData(prev => ({
                                                ...prev,
                                                category: e.target
                                                    .value as PersonalHistoryCategory,
                                            }))
                                        }
                                    >
                                        {Object.entries(
                                            PERSONAL_HISTORY_CATEGORY_LABELS
                                        ).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Status
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={personalFormData.status}
                                        onChange={e =>
                                            setPersonalFormData(prev => ({
                                                ...prev,
                                                status: e.target
                                                    .value as ConditionStatus,
                                            }))
                                        }
                                    >
                                        {Object.entries(
                                            CONDITION_STATUS_LABELS
                                        ).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Condição{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Descreva a condição..."
                                    value={personalFormData.condition}
                                    onChange={e =>
                                        setPersonalFormData(prev => ({
                                            ...prev,
                                            condition: e.target.value,
                                        }))
                                    }
                                    required
                                />
                            </div>

                            {/* ICD Search */}
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Código CID
                                    </span>
                                </label>
                                {selectedIcd ? (
                                    <div className="flex items-center gap-2 p-3 bg-base-200 rounded-lg">
                                        <span className="flex-1">
                                            <strong>{selectedIcd.code}</strong>{' '}
                                            - {selectedIcd.description}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={clearSelectedIcd}
                                            className="btn btn-ghost btn-sm btn-square"
                                        >
                                            <MdClose className="text-lg" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className="input input-bordered w-full pr-10"
                                            placeholder="Buscar código CID..."
                                            value={icdSearchQuery}
                                            onChange={e =>
                                                setIcdSearchQuery(
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <MdSearch className="absolute z-10 right-3 top-1/2 -translate-y-1/2 text-base-content/50" />
                                        {icdSearchResults.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {icdSearchResults.map(icd => (
                                                    <button
                                                        key={icd.id}
                                                        type="button"
                                                        onClick={() =>
                                                            selectIcd(icd)
                                                        }
                                                        className="w-full px-4 py-2 text-left hover:bg-base-200 text-sm"
                                                    >
                                                        <strong>
                                                            {icd.code}
                                                        </strong>{' '}
                                                        - {icd.description}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Data do Diagnóstico
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={personalFormData.diagnosisDate}
                                        onChange={e =>
                                            setPersonalFormData(prev => ({
                                                ...prev,
                                                diagnosisDate: e.target.value,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Data de Resolução
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={personalFormData.resolutionDate}
                                        onChange={e =>
                                            setPersonalFormData(prev => ({
                                                ...prev,
                                                resolutionDate: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tratamento
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Descreva o tratamento..."
                                    rows={2}
                                    value={personalFormData.treatment}
                                    onChange={e =>
                                        setPersonalFormData(prev => ({
                                            ...prev,
                                            treatment: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Medicamentos Atuais
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Liste os medicamentos..."
                                    rows={2}
                                    value={personalFormData.currentMedications}
                                    onChange={e =>
                                        setPersonalFormData(prev => ({
                                            ...prev,
                                            currentMedications: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Observações
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Observações adicionais..."
                                    rows={2}
                                    value={personalFormData.notes}
                                    onChange={e =>
                                        setPersonalFormData(prev => ({
                                            ...prev,
                                            notes: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    onClick={closePersonalModal}
                                    className="btn btn-ghost"
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-secondary gap-2"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={closePersonalModal}
                    ></div>
                </div>
            )}

            {/* Family History Modal */}
            {familyModalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingFamilyId
                                    ? 'Editar Antecedente Familiar'
                                    : 'Novo Antecedente Familiar'}
                            </h3>
                            <button
                                onClick={closeFamilyModal}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form
                            onSubmit={handleFamilySubmit}
                            className="space-y-4"
                        >
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Profissional Responsável{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={
                                            familyFormData.professionalId
                                                ? Number(
                                                      familyFormData.professionalId
                                                  )
                                                : null
                                        }
                                        onChange={id =>
                                            setFamilyFormData(prev => ({
                                                ...prev,
                                                professionalId: id
                                                    ? String(id)
                                                    : '',
                                            }))
                                        }
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={!!editingFamilyId}
                                    />
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Parentesco{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={familyFormData.relationship}
                                    onChange={e =>
                                        setFamilyFormData(prev => ({
                                            ...prev,
                                            relationship: e.target
                                                .value as FamilyRelationship,
                                        }))
                                    }
                                >
                                    {Object.entries(
                                        FAMILY_RELATIONSHIP_LABELS
                                    ).map(([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Condição{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Descreva a condição..."
                                    value={familyFormData.condition}
                                    onChange={e =>
                                        setFamilyFormData(prev => ({
                                            ...prev,
                                            condition: e.target.value,
                                        }))
                                    }
                                    required
                                />
                            </div>

                            {/* ICD Search */}
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Código CID
                                    </span>
                                </label>
                                {selectedIcd ? (
                                    <div className="flex items-center gap-2 p-3 bg-base-200 rounded-lg">
                                        <span className="flex-1">
                                            <strong>{selectedIcd.code}</strong>{' '}
                                            - {selectedIcd.description}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={clearSelectedIcd}
                                            className="btn btn-ghost btn-sm btn-square"
                                        >
                                            <MdClose className="text-lg" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className="input input-bordered w-full pr-10"
                                            placeholder="Buscar código CID..."
                                            value={icdSearchQuery}
                                            onChange={e =>
                                                setIcdSearchQuery(
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <MdSearch className="absolute z-10 right-3 top-1/2 -translate-y-1/2 text-base-content/50" />
                                        {icdSearchResults.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {icdSearchResults.map(icd => (
                                                    <button
                                                        key={icd.id}
                                                        type="button"
                                                        onClick={() =>
                                                            selectIcd(icd)
                                                        }
                                                        className="w-full px-4 py-2 text-left hover:bg-base-200 text-sm"
                                                    >
                                                        <strong>
                                                            {icd.code}
                                                        </strong>{' '}
                                                        - {icd.description}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Idade de Início
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        className="input input-bordered w-full"
                                        placeholder="Idade em anos"
                                        min="0"
                                        value={familyFormData.ageAtOnset}
                                        onChange={e =>
                                            setFamilyFormData(prev => ({
                                                ...prev,
                                                ageAtOnset: e.target.value,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label cursor-pointer justify-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary"
                                            checked={familyFormData.deceased}
                                            onChange={e =>
                                                setFamilyFormData(prev => ({
                                                    ...prev,
                                                    deceased: e.target.checked,
                                                }))
                                            }
                                        />
                                        <span className="label-text font-medium">
                                            Falecido(a)
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {familyFormData.deceased && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Idade ao Falecer
                                            </span>
                                        </label>
                                        <input
                                            type="number"
                                            className="input input-bordered w-full"
                                            placeholder="Idade em anos"
                                            min="0"
                                            value={familyFormData.deathAge}
                                            onChange={e =>
                                                setFamilyFormData(prev => ({
                                                    ...prev,
                                                    deathAge: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Causa do Óbito
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Causa"
                                            value={familyFormData.deathCause}
                                            onChange={e =>
                                                setFamilyFormData(prev => ({
                                                    ...prev,
                                                    deathCause: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Observações
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Observações adicionais..."
                                    rows={3}
                                    value={familyFormData.notes}
                                    onChange={e =>
                                        setFamilyFormData(prev => ({
                                            ...prev,
                                            notes: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    onClick={closeFamilyModal}
                                    className="btn btn-ghost"
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-secondary gap-2"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={closeFamilyModal}
                    ></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeletePersonalModal}
                onClose={() => {
                    setShowDeletePersonalModal(false)
                    setPersonalHistoryToDelete(null)
                }}
                onConfirm={confirmDeletePersonal}
                title="Excluir antecedente"
                message="Tem certeza que deseja excluir este antecedente?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />

            <ConfirmModal
                isOpen={showDeleteFamilyModal}
                onClose={() => {
                    setShowDeleteFamilyModal(false)
                    setFamilyHistoryToDelete(null)
                }}
                onConfirm={confirmDeleteFamily}
                title="Excluir antecedente familiar"
                message="Tem certeza que deseja excluir este antecedente familiar?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
